import { ReactNode, createContext, useContext, useRef, RefObject, useEffect, useState } from 'react';

/**
 * Interface defining the Layout context properties and methods
 * @interface ILayoutContext
 */
export interface ILayoutContext {
  /** Reference to the main container element */
  containerRef: RefObject<HTMLDivElement | null>;

  /** When true, MainLayout must not scroll the outer container (nested panes handle their own scroll). */
  containerScrollLocked: boolean;
  setContainerScrollLocked: (locked: boolean) => void;

  /** When true (and on mobile), MainLayout skips rendering the page breadcrumb header. */
  headerHidden: boolean;
  setHeaderHidden: (hidden: boolean) => void;

  /**
   * Scrolls the container to the specified position
   * @param {ScrollToOptions} [options] - Standard ScrollToOptions object
   */
  scrollTo: (options?: ScrollToOptions) => void;

  /**
   * Convenience method to scroll the container to the top with smooth animation
   */
  scrollToTop: () => void;
  scrollOffset: number;
  didScroll: boolean;
}

/**
 * Context for accessing and controlling layout-related functionality
 * The default values are placeholders that will be overridden by the provider
 */
const LayoutContext = createContext<ILayoutContext>({
  containerRef: { current: null },
  containerScrollLocked: false,
  setContainerScrollLocked: () => {},
  headerHidden: false,
  setHeaderHidden: () => {},
  scrollTo: () => {},
  scrollToTop: () => {},
  scrollOffset: 0,
  didScroll: false,
});

/**
 * Provider component for the LayoutContext
 *
 * This provider creates and maintains a reference to the main container element
 * and provides methods to control scrolling behavior.
 *
 * @param {Object} props - Component props
 * @param {ReactNode} props.children - Child components
 */
export function LayoutContextProvider({ children }: { children: ReactNode | ReactNode[] }) {
  // Create a ref to the container element
  const containerRef = useRef<HTMLDivElement>(null);

  const [scrollOffset, setScrollOffset] = useState(0);
  const [containerScrollLocked, setContainerScrollLocked] = useState(false);
  const [headerHidden, setHeaderHidden] = useState(false);

  /**
   * Scrolls the container to the specified position
   * @param {ScrollToOptions} [options] - Standard ScrollToOptions object
   */
  const scrollTo = (options?: ScrollToOptions) => {
    if (containerRef.current) {
      containerRef.current.scrollTo(options || { top: 0 });
    }
  };

  const handleScroll = () => {
    if (!containerRef.current) return;
    setScrollOffset(containerRef.current.scrollTop);
  };

  useEffect(() => {
    if (!containerRef.current) return;
    containerRef.current.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      containerRef.current?.removeEventListener('scroll', handleScroll);
    };
  }, [containerRef]);

  /**
   * Scrolls the container to the top with smooth animation
   */
  const scrollToTop = () => {
    if (containerRef.current) {
      containerRef.current.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    }
  };

  return (
    <LayoutContext.Provider
      value={{
        containerRef,
        containerScrollLocked,
        setContainerScrollLocked,
        headerHidden,
        setHeaderHidden,
        scrollTo,
        scrollToTop,
        scrollOffset,
        didScroll: scrollOffset > 250,
      }}
    >
      {children}
    </LayoutContext.Provider>
  );
}

/**
 * Custom hook to access the LayoutContext
 *
 * This hook provides access to the container reference and scroll control methods.
 * It must be used within a component that is a child of LayoutContextProvider.
 *
 * @returns {ILayoutContext} The layout context object
 * @throws {Error} If used outside of a LayoutContextProvider
 *
 * ```
 */
export const useLayout = () => {
  const context = useContext(LayoutContext);
  if (context === undefined) {
    throw new Error('useLayout must be used within a LayoutContextProvider');
  }
  return context;
};
